<template>
  <div class="mt-15 cols">
    <h2 class="ml-0">{{ $t('settings.on-board.users.task-schedules.title') }}</h2>
    <button type="button" class="button-save fit-content mb-10 task-schedules-create-button" @click="openCreateTaskSchedulePopup">
      {{ $t('settings.on-board.users.task-schedules.create') }}
    </button>
    <div v-if="taskScheduleGroups.length === 0" class="pt-10">
      {{ $t('settings.on-board.users.task-schedules.empty') }}
    </div>
    <Accordion
        v-for="driverGroup in taskScheduleGroups"
        :key="driverGroup.driverId"
        class="mb-10"
        :expanded="false"
    >
      <template #title>
        <span class="pr-5">{{ $t('dashboard.quick-map.info-window.driver') }}:</span>
        <span class="font-bold">{{ driverGroup.driverName }}</span>
      </template>
      <template #content>
        <Accordion
            v-for="vehicleGroup in driverGroup.vehicles"
            :key="vehicleGroup.vehicleId"
            class="mb-10"
            :expanded="false"
        >
          <template #title>
            <span class="pr-5">{{ $t('settings.car-fleet.vehicle-name') }}:</span>
            <span class="font-bold">{{ getVehicleTitle(vehicleGroup) }}</span>
          </template>
          <template #actions>
            <span
                class="task-schedules-status-label mr-5 font-bold font-size-15"
                :class="{'inactive': !vehicleGroup.active}"
            >
              {{ $t(vehicleGroup.active ? 'settings.on-board.users.task-schedules.active' : 'settings.on-board.users.task-schedules.inactive') }}
            </span>
            <ToggleSwitch
                class="pr-5"
                :model-value="vehicleGroup.active"
                :disabled="isTaskScheduleToggleDisabled(vehicleGroup)"
                @click.stop
                @change="toggleTaskScheduleActive(vehicleGroup, $event)"
            />
            <button
                type="button"
                class="transparent pr-5 pl-5"
                :title="$t('generic.copy')"
                @click.stop="openCopyTaskSchedulePopup(vehicleGroup)"
            >
              <i class="bi bi-copy font-size-20"></i>
            </button>
            <button
                type="button"
                class="transparent pr-5 pl-5"
                :title="$t('generic.edit')"
                @click.stop="openEditTaskSchedulePopup(vehicleGroup)"
            >
              <i class="bi bi-pencil-fill font-size-20"></i>
            </button>
            <button
                type="button"
                class="transparent pr-5 pl-5"
                :title="$t('generic.delete')"
                @click.stop="deleteTaskSchedule(vehicleGroup)"
            >
              <i class="bi bi-trash font-size-20"></i>
            </button>
          </template>
          <template #content>
            <DataGrid
                :data="vehicleGroup.geofences"
                :data-model="geofencesDataGridModel"
                :interactive="false"
            />
          </template>
        </Accordion>
      </template>
    </Accordion>
  </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import Accordion from "@/components/pages/partials/Accordion.vue";
import DataGrid from "@/components/pages/partials/DataGrid.vue";
import ToggleSwitch from "@/components/pages/partials/ToggleSwitch.vue";
import {dataManipulator, entityDataManager} from "@/assets/js/data-manipulation";
import {actions} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";
import {popups} from "@/assets/js/popups";
import {createApp} from "vue";
import TaskScheduleFormPopup from "@/components/pages/client/TaskScheduleFormPopup.vue";
import i18n from "@/assets/js/vue/i18n";
import store from "@/assets/js/vue/vuex";

export default {
  name: 'TaskSchedules',
  components: {Accordion, DataGrid, ToggleSwitch},
  data() {
    return {
      togglingTaskScheduleIds: [],
    };
  },
  computed: {
    ...mapState(['drivers', 'vehicles', 'taskSchedules']),
    ...mapGetters(['allGeoFences']),
    taskScheduleGroups() {
      const groupsByDriverId = new Map();

      (this.taskSchedules ?? []).forEach(schedule => {
        const driverId = schedule.driverId;
        const vehicleId = schedule.vehicleId;

        if (!groupsByDriverId.has(driverId)) {
          groupsByDriverId.set(driverId, {
            driverId,
            driverName: this.getDriverName(driverId),
            vehicles: new Map(),
          });
        }

        const driverGroup = groupsByDriverId.get(driverId);
        driverGroup.vehicles.set(vehicleId, {
          id: schedule.id,
          driver: entityDataManager.getById(this.drivers, driverId),
          vehicle: entityDataManager.getById(this.vehicles, vehicleId),
          vehicleId,
          vehicleName: this.getVehicleName(vehicleId),
          periodLengthDays: schedule.periodLengthDays,
          active: schedule.active ?? true,
          geofences: this.mapScheduleGeofences(schedule.taskScheduleGeofences ?? []),
        });
      });

      return Array.from(groupsByDriverId.values())
          .map(driverGroup => ({
            ...driverGroup,
            vehicles: Array.from(driverGroup.vehicles.values())
                .sort((a, b) => a.vehicleName.localeCompare(b.vehicleName)),
          }))
          .sort((a, b) => a.driverName.localeCompare(b.driverName));
    },
    geofencesDataGridModel() {
      return {
        headers: {
          [this.$t('geo-fences.name')]: 'name',
          [this.$t('settings.on-board.users.task-schedules.days-of-week')]: 'daysOfWeek',
        },
        values: {
          name: item => item.name,
          daysOfWeek: item => this.formatDaysOfWeek(item.daysOfWeek),
        },
      };
    },
  },
  methods: {
    async openCreateTaskSchedulePopup() {
      await this.openTaskSchedulePopup(
          this.$t('settings.on-board.users.task-schedules.create'),
          formData => this.createTaskSchedule(formData),
      );
    },
    async openEditTaskSchedulePopup(vehicleGroup) {
      await this.openTaskSchedulePopup(
          this.$t('generic.edit'),
          formData => this.updateTaskSchedule(vehicleGroup.id, formData),
          {
            id: vehicleGroup.id,
            driver: vehicleGroup.driver,
            vehicle: vehicleGroup.vehicle,
            periodLengthDays: vehicleGroup.periodLengthDays,
            geofences: vehicleGroup.geofences,
          },
      );
    },
    async openCopyTaskSchedulePopup(vehicleGroup) {
      await this.openTaskSchedulePopup(
          this.$t('settings.on-board.users.task-schedules.create'),
          formData => this.createTaskSchedule(formData),
          this.getTaskScheduleCopyInitialData(vehicleGroup),
      );
    },
    async openTaskSchedulePopup(title, onSaveHandler, initialData = null) {
      const wrapper = document.createElement('div');
      let componentApp = null;

      const onSave = async (formData) => {
        try {
          await onSaveHandler(formData);
          await this.$swal.close();
        }
        catch (e) {
          notification.error(this.$t(e.message));
        }
      };

      const onCancel = async () => {
        await this.$swal.close();
      };

      componentApp = createApp(TaskScheduleFormPopup, {
        initialData,
        onSave,
        onCancel,
      });
      componentApp.use(i18n);
      componentApp.use(store);
      componentApp.mount(wrapper);

      await this.$swal.fire({
        title,
        html: wrapper,
        width: '700px',
        padding: '5px',
        focusConfirm: false,
        showConfirmButton: false,
        showCancelButton: false,
        didOpen: () => document.activeElement?.blur(),
        didClose: () => componentApp?.unmount(),
      });
    },
    async createTaskSchedule(formData) {
      await this.$store.dispatch(actions.CREATE_TASK_SCHEDULE, formData);
      await this.$store.dispatch(actions.GET_TASK_SCHEDULES);
      notification.success(this.$t('generic.save.success'));
    },
    async updateTaskSchedule(id, formData) {
      await this.$store.dispatch(actions.UPDATE_TASK_SCHEDULE, {
        ...formData,
        id,
      });
      await this.$store.dispatch(actions.GET_TASK_SCHEDULES);
      notification.success(this.$t('generic.save.success'));
    },
    getTaskScheduleCopyInitialData(vehicleGroup) {
      return {
        driver: vehicleGroup.driver,
        vehicle: vehicleGroup.vehicle,
        periodLengthDays: vehicleGroup.periodLengthDays,
        geofences: vehicleGroup.geofences.map(geoFence => ({
          ...geoFence,
          id: null,
        })),
      };
    },
    async toggleTaskScheduleActive(vehicleGroup, active) {
      if (this.isTaskScheduleToggleDisabled(vehicleGroup)) {
        return;
      }

      this.togglingTaskScheduleIds.push(vehicleGroup.id);

      try {
        await this.$store.dispatch(
            active ? actions.ACTIVATE_TASK_SCHEDULE : actions.DEACTIVATE_TASK_SCHEDULE,
            {id: vehicleGroup.id},
        );
        await this.$store.dispatch(actions.GET_TASK_SCHEDULES);
      }
      catch (e) {
        notification.error(this.$t(e.message));
      }
      finally {
        this.togglingTaskScheduleIds = this.togglingTaskScheduleIds.filter(id => id !== vehicleGroup.id);
      }
    },
    isTaskScheduleToggleDisabled(vehicleGroup) {
      return this.togglingTaskScheduleIds.includes(vehicleGroup.id);
    },
    async deleteTaskSchedule(vehicleGroup) {
      const result = await popups.yesNoConfirmation(this.$swal, this.$t('generic.delete.confirm'));
      if (!result?.isConfirmed) {
        return;
      }

      try {
        await this.$store.dispatch(actions.DELETE_TASK_SCHEDULE, {id: vehicleGroup.id});
        await this.$store.dispatch(actions.GET_TASK_SCHEDULES);
        notification.success(this.$t('generic.delete.success'));
      }
      catch (e) {
        notification.error(this.$t(e.message));
      }
    },
    getDriverName(driverId) {
      return entityDataManager.getById(this.drivers, driverId)?.name
          ?? this.$t('settings.on-board.users.task-schedules.unknown-driver');
    },
    getVehicleName(vehicleId) {
      const vehicle = entityDataManager.getById(this.vehicles, vehicleId);

      return vehicle?.fullName
          ?? vehicle?.nickname
          ?? this.$t('settings.on-board.users.task-schedules.unknown-vehicle');
    },
    getVehicleTitle(vehicleGroup) {
      return this.$t('settings.on-board.users.task-schedules.vehicle-period-title', {
        vehicleName: vehicleGroup.vehicleName,
        days: vehicleGroup.periodLengthDays,
        dayLabel: this.$t(dataManipulator.getDaysTranslationKey(vehicleGroup.periodLengthDays)),
      });
    },
    mapScheduleGeofences(scheduleGeofences) {
      return [...scheduleGeofences]
          .sort((a, b) => (a.numbering ?? 0) - (b.numbering ?? 0))
          .map(scheduleGeofence => {
            const geofence = entityDataManager.getById(this.allGeoFences, scheduleGeofence.geofenceId);
            const name = geofence?.name ?? this.$t('settings.on-board.users.task-schedules.unknown-geofence');
            const description = geofence?.description ?? '';

            return {
              id: scheduleGeofence?.id ?? null,
              geofenceId: scheduleGeofence.geofenceId,
              name: description ? `${name} - ${description}` : name,
              daysOfWeek: scheduleGeofence.daysOfWeek,
            };
          });
    },
    formatDaysOfWeek(daysOfWeek) {
      return Array.isArray(daysOfWeek)
          ? daysOfWeek
              .filter(day => day >= 1 && day <= 7)
              .map(day => this.$t(`settings.on-board.users.task-schedules.weekday-button.${day}`))
              .join(', ')
          : '';
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';
.task-schedules-create-button {
  width: 200px;
}

.task-schedules-status-label {
  color: $tracking-accent;

  &.inactive {
    color: gray;
  }
}
</style>
